/**
 * 建站舱站点页「新闻栏」(1009 一期):这个站在官网上发了什么、排了什么。
 *
 * ★ 主入口在新闻舱:这里只做查看与跳转 ——「从新闻舱选稿」挑一篇已出稿的文章 → 去它的预览页发(预选本站);
 *   「新写一篇」→ 新闻舱创建页(带上本站,出稿后发布弹窗预选)。改期 / 取消 / 下线 / 同步都在预览页那块「官网」里做。
 * ★ 排期按日期分组;模板没有动态新闻列表时如实提示「只能经链接访问」。
 * ★ 宿主上不是我们推的文章(模板样例 / 别处写入)只读列出;宿主暂时查不到就说查不到,不当成「没有」。
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarClock, ExternalLink, Loader2, PenLine, Search } from "lucide-react";

import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { Skeleton } from "@/shared/ui/skeleton";
import { errorText } from "@/shared/lib/apiErrors";
import { websiteService } from "@/features/website/api/websiteService";
import { SiteShell } from "@/features/website/ui/SiteShell";
import { reporterService } from "@/features/reporter/api/reporterService";
import {
  reporterSiteService,
  type SitePublication,
} from "@/features/reporter/api/reporterSiteService";
import { GENRE_LABEL } from "@/features/reporter/model/labels";
import {
  canPublishGenre,
  groupByDate,
  shortTime,
  siteHost,
  siteListText,
} from "@/features/reporter/model/sitePublication";
import { SiteStatusPill } from "@/features/reporter/ui/SitePublicationPanel";

const PENDING = new Set(["SCHEDULED", "PUBLISHING", "FAILED"]);

export default function SiteNewsPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [picking, setPicking] = useState(false);

  const siteQ = useQuery({
    queryKey: ["website-site", id],
    queryFn: () => websiteService.getSite(id),
  });
  const newsQ = useQuery({
    queryKey: ["reporter-site-news", id],
    queryFn: () => reporterSiteService.bySite(id),
    enabled: !!id,
  });
  const news = newsQ.data;
  const pending = useMemo(
    () => (news?.items ?? []).filter((i) => PENDING.has(i.status)),
    [news?.items]
  );
  const done = useMemo(
    () => (news?.items ?? []).filter((i) => !PENDING.has(i.status)),
    [news?.items]
  );
  const groups = useMemo(
    () => groupByDate(pending, (p) => p.nextAttemptAt ?? p.scheduledAt),
    [pending]
  );

  if (siteQ.isLoading) return <Skeleton className="h-64 w-full" />;
  if (siteQ.isError || !siteQ.data) {
    return (
      <QueryErrorBanner
        isError
        message={errorText(siteQ.error)}
        onRetry={() => void siteQ.refetch()}
      />
    );
  }
  const site = siteQ.data;
  // 1009 D:同名站靠域名分清 —— 标题「站名 · 域名 · 新闻栏」(SiteShell 拼「站名 · pageTitle」)
  const host = siteHost(news?.site.siteUrl);
  const openDraft = (p: SitePublication) => navigate(`/ai-jizhe/preview/${p.draftId}?site=${id}`);

  return (
    <SiteShell
      site={site}
      pageTitle={host ? `${host} · 新闻栏` : "新闻栏"}
      subtitle="新闻舱写的文章发到这个站点的新闻栏;发布、改期、下线都在文章预览页的「官网」里操作"
      actions={
        <>
          <Button variant="outline" onClick={() => setPicking(true)} data-site-news-pick="">
            <Search className="mr-2 h-4 w-4" />
            从新闻舱选稿
          </Button>
          <Button onClick={() => navigate(`/ai-jizhe/create?site=${id}`)} data-site-news-new="">
            <PenLine className="mr-2 h-4 w-4" />
            新写一篇
          </Button>
        </>
      }
    >
      {newsQ.isLoading && <Skeleton className="h-40 w-full" />}
      <QueryErrorBanner
        isError={newsQ.isError}
        message={newsQ.isError ? errorText(newsQ.error) : undefined}
        onRetry={() => void newsQ.refetch()}
      />
      {news && (
        <div className="space-y-6" data-site-news="">
          {news.site.siteUrl && (
            <p className="text-xs text-muted-foreground" data-site-news-list-mode="">
              {siteListText(news.site.articleListSupported)}
              {news.site.articleListSupported === true &&
                ":从新闻舱发上来的文章会自动出现在官网新闻列表"}
            </p>
          )}
          {news.site.hint && (
            <p className="rounded-lg border border-warning/50 bg-warning/5 px-3 py-2 text-xs text-warning">
              {news.site.hint}
            </p>
          )}

          <section>
            <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
              <CalendarClock className="h-4 w-4" />
              排期
              <span className="text-xs font-normal text-muted-foreground">{pending.length} 篇</span>
            </h2>
            {groups.length === 0 ? (
              <p className="text-xs text-muted-foreground">没有排期中的文章。</p>
            ) : (
              <div className="space-y-3">
                {groups.map((g) => (
                  <div key={g.date} data-site-news-day={g.date}>
                    <div className="mb-1 text-xs font-semibold text-muted-foreground">{g.date}</div>
                    <ul className="space-y-1.5">
                      {g.rows.map((p) => (
                        <Row key={p.id} p={p} onOpen={() => openDraft(p)} />
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className="mb-2 text-sm font-semibold">
              已发 / 已下线
              <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                {done.length} 篇
              </span>
            </h2>
            {done.length === 0 ? (
              <p className="text-xs text-muted-foreground">还没有从新闻舱发过文章。</p>
            ) : (
              <ul className="space-y-1.5">
                {done.map((p) => (
                  <Row key={p.id} p={p} onOpen={() => openDraft(p)} />
                ))}
              </ul>
            )}
          </section>

          <section>
            <h2 className="mb-2 text-sm font-semibold">官网上的其他文章</h2>
            {news.hostError ? (
              <p className="text-xs text-muted-foreground">{news.hostError}</p>
            ) : news.hostArticles.length === 0 ? (
              <p className="text-xs text-muted-foreground">没有。</p>
            ) : (
              <ul className="space-y-1.5">
                {news.hostArticles.map((h) => (
                  <li
                    key={h.hostArticleId}
                    className="flex items-center gap-2 rounded-lg border border-border/70 px-3 py-2 text-xs"
                  >
                    <span className="min-w-0 flex-1 truncate">{h.title || "(无标题)"}</span>
                    <span className="text-muted-foreground">
                      {h.status === "published" ? "已上线" : "草稿"}
                    </span>
                    {h.url && (
                      <a
                        href={h.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      {picking && (
        <PickDraftDialog
          onClose={() => setPicking(false)}
          onPick={(draftId) => navigate(`/ai-jizhe/preview/${draftId}?site=${id}`)}
        />
      )}
    </SiteShell>
  );
}

function Row({ p, onOpen }: { p: SitePublication; onOpen: () => void }) {
  return (
    <li
      className="flex flex-wrap items-center gap-2 rounded-lg border border-border/70 px-3 py-2 text-xs"
      data-site-news-item={p.id}
    >
      <button
        type="button"
        className="min-w-0 flex-1 truncate text-left font-medium hover:underline"
        onClick={onOpen}
      >
        {p.articleTitle || "(未命名文章)"}
      </button>
      <SiteStatusPill p={p} />
      {p.publishedAt && p.status === "PUBLISHED" && (
        <span className="text-muted-foreground">{shortTime(p.publishedAt)}</span>
      )}
      {p.outdated && <span className="text-warning">文章有新版本</span>}
      {p.lastError && <span className="w-full text-destructive">{p.lastError}</span>}
      {p.siteUrl && p.status === "PUBLISHED" && (
        <a
          href={p.siteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
    </li>
  );
}

/** 从新闻舱选一篇已出稿、体裁可发的文章。 */
function PickDraftDialog({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (draftId: number) => void;
}) {
  const q = useQuery({
    queryKey: ["reporter-drafts-pick"],
    queryFn: () => reporterService.listDrafts(50, false),
  });
  const rows = (q.data ?? []).filter(
    (d) => canPublishGenre(d.genre) && (d.status === "GENERATED" || d.status === "EDITED")
  );
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-site-news-picker="">
        <DialogHeader>
          <DialogTitle>从新闻舱选稿</DialogTitle>
          <DialogDescription>
            只列已出稿的新闻 / 品牌 / 活动 / 产品文章(小红书、问答不能发到官网)。
          </DialogDescription>
        </DialogHeader>
        {q.isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : q.isError ? (
          <p className="text-sm text-destructive">{errorText(q.error)}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">没有可发的文章,先去新闻舱写一篇。</p>
        ) : (
          <ul className="max-h-80 space-y-1.5 overflow-y-auto">
            {rows.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-lg border border-border/70 px-3 py-2 text-left text-xs hover:bg-muted"
                  onClick={() => onPick(d.id)}
                >
                  <span className="min-w-0 flex-1 truncate">{d.title || `#${d.id}`}</span>
                  <span className="text-muted-foreground">{GENRE_LABEL[d.genre] ?? d.genre}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
